import {Flag} from 'platform/components';
import {Box, Heading, type HeadingLevel, HStack, Show, Text, VStack} from 'platform/foundation';

import type {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {ChartPaletteHue} from '../../types/ChartPaletteHue';

// A value that does not switch a series sits on a grey card.
const STATIC_CARD_COLORS: ChartPaletteHue['summary'] = {
  background: 'palettes.neutral.10.100',
  border: 'general.separator',
};

// The value has the size of a level 3 heading, but it ranks below the level 4 title of its card
// for screen reader users.
const VALUE_HEADING_LEVEL: HeadingLevel = 5;

// A line breaks only between the value and its rate flag. The flag moves under the value whole,
// and neither the value nor the rate splits inside.
const NO_BREAK_SPACE = '\u00a0';
const keepWhole = (text: string) => text.replaceAll(' ', NO_BREAK_SPACE);

// The subtle black flag has the neutral 40 background of the design (Figma 49446:238), so it stays
// visible on a hovered card, which is neutral 20. The subtle neutral flag is neutral 20 itself.

interface ChartSummaryCardProps {
  label: string;
  /** Formatted summary of the series, for example its total for the selected period. */
  value: string;
  /** Formatted rate shown in a flag after the value, for example its share of the total. */
  rate?: string;
  /** Formatted companion figure under the value, for example the stock card count of a bucket. */
  detail?: string;
  /** Defaults to the grey of a static card. */
  colors?: ChartPaletteHue['summary'];
  /** Mark in the top right corner, for example the state dot of a toggle card. */
  indicator?: ReactNode;
}

/**
 * One summary card above a chart. The static cards and the toggle cards share it, so both
 * keep the same size and type.
 */
export function ChartSummaryCard(props: ChartSummaryCardProps) {
  const colors = props.colors ?? STATIC_CARD_COLORS;
  const hasRate = isNotNilOrEmpty(props.rate);
  return (
    <Box
      height="100%"
      paddingVertical={4}
      paddingLeft={5}
      paddingRight={4}
      borderRadius="small"
      border="1px solid"
      borderColor={colors.border}
      backgroundColor={colors.background}
      transition="border-color 150ms ease-out, background-color 150ms ease-out"
    >
      <HStack spacing={2} align="flex-start">
        <VStack spacing={2} grow={1}>
          <Text size="xSmall" color="secondary">
            {props.label}
          </Text>
          <HStack spacing={2} align="center" wrap>
            <Heading size={3} headingLevel={VALUE_HEADING_LEVEL}>
              {hasRate ? keepWhole(props.value) : props.value}
            </Heading>
            <Show when={isNotNilOrEmpty(props.rate) ? props.rate : null}>
              {(rate) => <Flag label={keepWhole(rate)} colorScheme="black" isSubtle />}
            </Show>
          </HStack>
          <Show when={isNotNilOrEmpty(props.detail) ? props.detail : null}>
            {(detail) => (
              <Text size="small" color="secondary">
                {detail}
              </Text>
            )}
          </Show>
        </VStack>
        {props.indicator}
      </HStack>
    </Box>
  );
}
